import { useState } from "react";
import { toast } from "sonner";
import { errMsg } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { useAuth } from "../lib/auth";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";

export const AuthDialog = ({ open, onOpenChange }) => {
  const { t } = useI18n();
  const { login, register } = useAuth();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "", phone: "", address: "" });
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      if (mode === "login") {
        const u = await login(form.email.trim(), form.password);
        toast.success(`${t("login_ok")} ${u.name}`);
      } else {
        const u = await register({
          name: form.name.trim(),
          email: form.email.trim(),
          password: form.password,
          phone: form.phone.trim(),
          address: form.address.trim(),
        });
        toast.success(`${t("register_ok")} ${u.name}`);
      }
      onOpenChange(false);
    } catch (e) {
      toast.error(errMsg(e, "Gagal masuk"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="auth-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">
            {mode === "login" ? t("login") : t("register")}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          {mode === "register" && (
            <Input
              data-testid="auth-name-input"
              placeholder={t("full_name")}
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
          )}
          <Input
            data-testid="auth-email-input"
            type="email"
            placeholder={t("email")}
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
          />
          <Input
            data-testid="auth-password-input"
            type="password"
            placeholder={t("password")}
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            onKeyDown={(e) => e.key === "Enter" && submit()}
          />
          {mode === "register" && (
            <>
              <Input
                data-testid="auth-phone-input"
                placeholder={t("cust_phone")}
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
              />
              <Input
                data-testid="auth-address-input"
                placeholder={t("cust_address")}
                value={form.address}
                onChange={(e) => setForm({ ...form, address: e.target.value })}
              />
            </>
          )}
          <Button
            className="w-full h-11"
            data-testid="auth-submit-btn"
            disabled={busy || !form.email.trim() || form.password.length < 6}
            onClick={submit}
          >
            {mode === "login" ? t("login") : t("register")}
          </Button>
          <div className="flex items-center gap-3">
            <span className="h-px flex-1 bg-gray-200" />
            <span className="text-xs text-muted-foreground">{t("or")}</span>
            <span className="h-px flex-1 bg-gray-200" />
          </div>
          <Button
            variant="outline"
            className="w-full h-11 gap-2"
            data-testid="auth-google-btn"
            onClick={() => {
              // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
              // THIS BREAKS THE AUTH
              const redirectUrl = window.location.origin + "/akun";
              window.location.href =
                `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
            }}
          >
            <svg viewBox="0 0 24 24" className="h-4 w-4" aria-hidden="true">
              <path fill="#4285F4" d="M23.5 12.3c0-.9-.1-1.5-.2-2.2H12v4.2h6.5c-.1 1.1-.8 2.7-2.3 3.8l-.1.1 3.4 2.6.2.1c2.1-2 3.3-4.9 3.3-8.6z" />
              <path fill="#34A853" d="M12 24c3.1 0 5.7-1 7.6-2.8l-3.6-2.8c-1 .7-2.3 1.2-4 1.2-3 0-5.6-2-6.5-4.8l-.1.1-3.6 2.7-.1.1C3.6 21.3 7.5 24 12 24z" />
              <path fill="#FBBC05" d="M5.5 14.8c-.2-.7-.4-1.5-.4-2.3s.1-1.6.4-2.3v-.1L1.8 7.3l-.1.1C.9 8.9.5 10.4.5 12.5s.4 3.6 1.2 5.1l3.8-2.8z" />
              <path fill="#EA4335" d="M12 4.7c2.1 0 3.6.9 4.4 1.7l3.2-3.1C17.7 1.5 15.1.5 12 .5 7.5.5 3.6 3.2 1.7 7.4l3.8 2.9C6.4 7.5 9 4.7 12 4.7z" />
            </svg>
            {t("google_login")}
          </Button>
          <button
            data-testid="auth-switch-mode-btn"
            className="w-full text-sm text-muted-foreground hover:text-foreground"
            onClick={() => setMode(mode === "login" ? "register" : "login")}
          >
            {mode === "login" ? t("no_account") : t("have_account")}
          </button>
          <p className="text-xs text-muted-foreground text-center">{t("guest_note")}</p>
        </div>
      </DialogContent>
    </Dialog>
  );
};
